import {
  afterNextRender,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  numberAttribute,
} from '@angular/core';
import {
  computeFillHeight,
  findOutermostBlock,
  findScrollContainer,
  measureContentBelow,
} from '../../utils/fill-height.utils';

@Directive({
  selector: '[appFillViewportHeight]',
  standalone: true,
})
export class FillViewportHeightDirective {
  readonly appFillViewportHeightMin = input(160, { transform: numberAttribute });

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      const container = findScrollContainer(this.host);
      let frame = 0;
      const schedule = (): void => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.apply(container));
      };

      const resizes = new ResizeObserver(schedule);
      resizes.observe(container);
      resizes.observe(findOutermostBlock(this.host, container));
      window.addEventListener('resize', schedule);
      this.apply(container);

      destroyRef.onDestroy(() => {
        cancelAnimationFrame(frame);
        resizes.disconnect();
        window.removeEventListener('resize', schedule);
      });
    });
  }

  private apply(container: HTMLElement): void {
    const containerBottom =
      container === document.scrollingElement
        ? window.innerHeight
        : container.getBoundingClientRect().bottom;
    const height = computeFillHeight({
      containerBottom,
      elementTop: this.host.getBoundingClientRect().top + container.scrollTop,
      contentBelow: measureContentBelow(this.host, container),
      minHeight: this.appFillViewportHeightMin(),
    });
    this.host.style.maxHeight = `${height}px`;
  }
}
